import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ClusterEfficiencyGauges from './ClusterEfficiencyGauges';
import ClusterTimeseriesChart from './ClusterTimeseriesChart';
import ClusterOnboardingDiagnostics from './ClusterOnboardingDiagnostics';

const ClusterTelemetrySection = ({
  selectedClusterId,
  onSelectCluster,
  clusters = [],
  getFirebaseToken,
  backendUrl,
  userTier = 'developer',
  onTriggerPricing
}) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('24h'); // '24h' | '7d'
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Active cluster ID resolution
  const activeClusterId = selectedClusterId || (clusters.length > 0 ? (typeof clusters[0] === 'object' ? clusters[0].id : clusters[0]) : 'default-cluster');

  const fetchTelemetry = useCallback(async () => {
    if (!activeClusterId || !backendUrl) return;

    setLoading(true);
    setError(null);

    try {
      const token = getFirebaseToken ? await getFirebaseToken() : '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const hours = period === '7d' ? 168 : 24;
      const summaryUrl = `${backendUrl}/api/v1/finops/clusters/${encodeURIComponent(activeClusterId)}/summary?period=${period}`;
      const timeseriesUrl = `${backendUrl}/api/v1/finops/clusters/${encodeURIComponent(activeClusterId)}/timeseries?hours=${hours}`;

      const [summaryRes, timeseriesRes] = await Promise.allSettled([
        axios.get(summaryUrl, { headers }),
        axios.get(timeseriesUrl, { headers })
      ]);

      if (summaryRes.status === 'fulfilled' && summaryRes.value?.data) {
        setSummary(summaryRes.value.data);
      } else {
        console.warn('[FinOps] Summary API fetch error:', summaryRes.reason);
        setSummary({
          cluster_id: activeClusterId,
          period,
          has_telemetry: false,
          status: 'CONNECTING',
          latest_metrics: { cpu_efficiency_percent: 0, memory_efficiency_percent: 0, timestamp: null },
          averages: { cpu_efficiency_percent: 0, memory_efficiency_percent: 0 },
          total_waste_usd: 0
        });
      }

      if (timeseriesRes.status === 'fulfilled' && Array.isArray(timeseriesRes.value?.data)) {
        setTimeseries(timeseriesRes.value.data);
      } else {
        console.warn('[FinOps] Timeseries API fetch error:', timeseriesRes.reason);
        setTimeseries([]);
      }
    } catch (err) {
      console.warn("Error loading cluster telemetry:", err);
      setSummary({
        cluster_id: activeClusterId,
        period,
        has_telemetry: false,
        status: 'CONNECTING',
        latest_metrics: { cpu_efficiency_percent: 0, memory_efficiency_percent: 0, timestamp: null },
        averages: { cpu_efficiency_percent: 0, memory_efficiency_percent: 0 },
        total_waste_usd: 0
      });
      setTimeseries([]);
    } finally {
      setLoading(false);
    }
  }, [activeClusterId, period, backendUrl, getFirebaseToken]);

  useEffect(() => {
    fetchTelemetry();
  }, [fetchTelemetry]);

  // Live polling diagnostic interval when CONNECTING
  useEffect(() => {
    let intervalId = null;
    if (summary && summary.status === 'CONNECTING') {
      intervalId = setInterval(() => {
        fetchTelemetry();
      }, 5000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [summary, fetchTelemetry]);

  // Unique list of cluster options mapped with fallback support
  const clusterOptions = [...clusters];
  if (activeClusterId && !clusterOptions.find(c => (typeof c === 'object' ? c.id : c) === activeClusterId)) {
    clusterOptions.push({ id: activeClusterId, name: activeClusterId, environment: '' });
  }

  const handleExportCsv = async () => {
    if (exportingCsv || !activeClusterId) return;
    setExportingCsv(true);
    try {
      const token = await getFirebaseToken();
      const url = `${backendUrl}/api/finops/clusters/${activeClusterId}/report/csv?period=${period}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `finops_report_${activeClusterId}_${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("CSV Export failed", err);
      alert(t('finops.telemetry.telemetry_error', 'Error exporting CSV'));
    } finally {
      setExportingCsv(false);
    }
  };

  const handleExportPdf = async () => {
    if (userTier === 'developer') {
      if (onTriggerPricing) onTriggerPricing();
      return;
    }

    if (exportingPdf || !activeClusterId) return;
    setExportingPdf(true);
    try {
      const token = await getFirebaseToken();
      const cleanLang = 'en';
      const url = `${backendUrl}/api/finops/clusters/${activeClusterId}/report/pdf?period=${period}&lang=${cleanLang}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `finops_executive_report_${activeClusterId}_${period}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("PDF Export failed", err);
      if (err.response && err.response.status === 403) {
        if (onTriggerPricing) onTriggerPricing();
      } else {
        alert(t('finops.reports.premium_gated', 'Premium account required or report generation failed.'));
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const isPdfLocked = userTier === 'developer';

  const selectStyle = {
    background: 'rgba(15, 23, 42, 0.8)',
    border: '1px solid rgba(99, 102, 241, 0.4)',
    color: '#F8FAFC',
    padding: '8px 14px',
    borderRadius: '10px',
    fontSize: '0.85rem',
    fontWeight: '600',
    outline: 'none',
    cursor: 'pointer'
  };

  const periodBtnStyle = (active) => ({
    padding: '7px 16px',
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: '700',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: active ? 'linear-gradient(135deg, #6366F1, #4F46E5)' : 'rgba(255,255,255,0.06)',
    color: active ? '#FFFFFF' : '#94A3B8',
    boxShadow: active ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'
  });

  const exportBtnStyle = {
    background: 'rgba(99, 102, 241, 0.1)',
    border: '1px solid rgba(99, 102, 241, 0.3)',
    color: '#818CF8',
    borderRadius: '8px',
    padding: '7px 12px',
    fontSize: '0.8rem',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(99, 102, 241, 0.08)'
  };

  return (
    <div style={{ marginBottom: '35px' }}>
      {/* Control Bar: Cluster Dropdown + Period Selector */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '20px',
        padding: '16px 20px',
        background: 'rgba(15, 23, 42, 0.5)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '14px',
        backdropFilter: 'blur(10px)'
      }}>
        {/* Left: Cluster Picker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '0.85rem', color: '#94A3B8', fontWeight: '600' }}>
            ☸️ {t('finops.telemetry.select_cluster')}:
          </span>
          <select
            value={activeClusterId}
            onChange={(e) => onSelectCluster && onSelectCluster(e.target.value)}
            style={selectStyle}
          >
            {clusterOptions.map((c) => {
              const cid = typeof c === 'object' ? c.id : c;
              const cname = typeof c === 'object' ? c.name : c;
              const cenv = typeof c === 'object' && c.environment ? ` (${c.environment})` : '';
              return (
                <option key={cid} value={cid} style={{ background: '#0F172A', color: '#FFF' }}>
                  {cname}{cenv}
                </option>
              );
            })}
          </select>
        </div>

        {/* Right: Period Switcher + Exports + Refresh */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', background: 'rgba(0,0,0,0.3)', padding: '3px', borderRadius: '10px' }}>
            <button
              onClick={() => setPeriod('24h')}
              style={periodBtnStyle(period === '24h')}
            >
              {t('finops.telemetry.period_24h')}
            </button>
            <button
              onClick={() => setPeriod('7d')}
              style={periodBtnStyle(period === '7d')}
            >
              {t('finops.telemetry.period_7d')}
            </button>
          </div>

          {/* Export Actions */}
          <button
            onClick={handleExportCsv}
            disabled={exportingCsv || loading}
            style={exportBtnStyle}
            title={t('finops.reports.export_csv')}
          >
            📥 {exportingCsv ? t('finops.reports.exporting') : t('finops.reports.export_csv')}
          </button>
          
          <button
            onClick={handleExportPdf}
            disabled={exportingPdf || loading}
            style={{
              ...exportBtnStyle,
              background: isPdfLocked ? 'rgba(148, 163, 184, 0.08)' : 'rgba(16, 185, 129, 0.1)',
              border: isPdfLocked ? '1px dashed rgba(148, 163, 184, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)',
              color: isPdfLocked ? '#94A3B8' : '#34D399'
            }}
            title={t('finops.reports.export_pdf')}
          >
            {isPdfLocked ? '🔒' : '📄'} {exportingPdf ? t('finops.reports.exporting') : t('finops.reports.export_pdf')}
          </button>

          <button
            onClick={fetchTelemetry}
            disabled={loading}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#94A3B8',
              borderRadius: '8px',
              padding: '7px 10px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem'
            }}
            title={t('finops.telemetry.refresh_tooltip')}
          >
            <span style={{
              display: 'inline-block',
              transform: loading ? 'rotate(360deg)' : 'none',
              transition: 'transform 0.6s ease'
            }}>
              🔄
            </span>
          </button>
        </div>
      </div>

      {/* Error banner if needed */}
      {error && (
        <div style={{
          padding: '10px 16px',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#FCA5A5',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: '0.85rem'
        }}>
          ⚠️ {t('finops.telemetry.telemetry_error')}: {error}
        </div>
      )}

      {/* Gauges & Timeseries Charts OR Onboarding Diagnostics */}
      {summary && summary.has_telemetry === false ? (
        <ClusterOnboardingDiagnostics
          summary={summary}
          loading={loading}
          onRefresh={fetchTelemetry}
        />
      ) : (
        <>
          {/* Gauges */}
          <ClusterEfficiencyGauges summary={summary} loading={loading} />

          {/* Timeseries Chart */}
          <ClusterTimeseriesChart timeseries={timeseries} loading={loading} period={period} />
        </>
      )}
    </div>
  );
};

export default ClusterTelemetrySection;
